/**
 * **A personal record, read on the card** (attention-queue R24;
 * `docs/design/zadania-progi-2026-10-02/`, view 4): the movement's picture entering from the card's
 * edge, beside it the movement's name, how many more of the session's movements hold a record, and
 * the result against the one before it — and on the desk the session's lines and four facts.
 *
 * ── What stands on the first paint, and what arrives ────────────────────────────────────────
 * The name and the count are the queue item's own. The result, the lines and the tiles come from
 * the session read the session cards already make (`useSessionReadout`), gated on the block being
 * on screen; every box they land in holds its height from the first frame. The picture is the
 * exercise library's (`useExerciseCover`).
 *
 * ── A movement with no picture gets no stand-in ─────────────────────────────────────────────
 * A trainer's own movement, one of the catalogue's without a cover or a poster, or a file that
 * failed to load: the words take the card's measure and the box keeps its height. A movement the
 * library holds lays its words out in the narrow column from the first frame — before the card is
 * on screen, before the read is asked — and keeps it until the library has answered, so a picture
 * that lands moves nothing. Only the answer „no picture" widens the column.
 *
 * On the desk the kind's heading stands INSIDE the hero, beside the photograph, which is why the
 * desk card does not draw it above the body (`model/taskContent`'s `headingStandsInBody`).
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dateTimeFormat } from '~/shared/lib/intlCache';
import { useOnScreen } from '~/shared/lib/useOnScreen';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import photo from '~/shared/ui/photoBox.module.css';
import fade from '~/shared/ui/photoFade.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useExerciseCover } from '../lib/useExerciseCover';
import { useSessionReadout } from '../lib/useSessionReadout';
import type { TaskDensity } from '../lib/taskWrites';
import type { AttentionItem } from '../model/attentionItems';
import { recordDetail } from '../model/recordDetail';
import type { TaskContent } from '../model/taskContent';
import type { RecordFacts } from '../model/triageShape';
import { BlockRead } from './BlockRead';
import { ExerciseLines } from './ExerciseLines';
import { TaskHeading } from './TaskHeading';
import own from './recordBlock.module.css';
import styles from './taskBlocks.module.css';

/** Three lines: what stands above the tiles at 1512×862; the rest is the „+N" line. */
const LINE_LIMIT = 3;

interface RecordBlockProps {
  readonly item: AttentionItem;
  readonly record: RecordFacts;
  readonly density: TaskDensity;
  readonly content: TaskContent;
}

export function RecordBlock({ item, record, density, content }: RecordBlockProps) {
  const { t, i18n } = useTranslation();
  const boxRef = useRef<HTMLDivElement>(null);
  const onScreen = useOnScreen(boxRef);
  const { detail, detailFailed, chipsPending } = useSessionReadout(
    {
      kind: 'session',
      freestyle: false,
      workoutExecutionId: item.workoutExecutionId,
      name: item.triage.sessionName,
      setsDone: null,
      setsTotal: null,
      durationSeconds: null,
    },
    onScreen,
  );
  const gate = useSkeletonGate(chipsPending);
  const cover = useExerciseCover(record.exerciseId, onScreen);
  // A file that would not load is a movement with no picture — the same answer, drawn the same way.
  const [failedCover, setFailedCover] = useState<string | null>(null);
  const picture = cover.url === failedCover ? null : cover.url;
  const view =
    detail === null ? null : recordDetail(record, detail, { t: (key, values) => t(key, values), locale: i18n.language });
  const desk = density === 'desk';
  const others = record.movements - 1;
  /** How many lines the wait reserves: a line per record movement, as many as the card draws. */
  const reservedLines = [own.lines1, own.lines2, own.lines3][Math.min(Math.max(record.movements, 1), LINE_LIMIT) - 1];

  const hero = (
    <div
      className={`${String(own.hero)} ${picture !== null || cover.pending ? String(own.onPhoto) : ''} ${desk ? '' : String(own.quick)}`}
    >
      {picture === null ? null : (
        <img
          className={`${String(photo.photo)} ${String(desk ? fade.edge : fade.leading)} ${String(own.photo)}`}
          src={picture}
          alt=""
          decoding="async"
          onError={() => {
            setFailedCover(picture);
          }}
        />
      )}
      <div className={own.text}>
        {desk ? <TaskHeading item={item} content={content} /> : null}
        <div className={own.words}>
          {record.exerciseName === null ? null : <p className={own.title}>{record.exerciseName}</p>}
          {others > 0 ? <p className={own.sub}>{t('web:tasks.blocks.record.others', { count: others })}</p> : null}
          <p className={`${String(own.value)} ${gate.waiting ? '' : gate.reveal}`}>
            {gate.waiting || view?.result == null ? null : (
              <>
                <span className={hidden.srOnly}>{t('web:tasks.blocks.record.resultLabel')} </span>
                {view.result}
                {view.previous === null ? null : (
                  <span className={own.previous}> · {t('web:tasks.blocks.record.previous', { result: view.previous })}</span>
                )}
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );

  if (!desk) {
    return (
      <div className={`${String(styles.stack)} ${String(styles.quick)} ${String(own.quickBody)}`} ref={boxRef} aria-busy={gate.waiting}>
        {gate.waiting ? <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span> : null}
        {hero}
      </div>
    );
  }

  const tallies = detail?.tallies ?? null;
  return (
    <div className={`${String(styles.stack)} ${String(styles.desk)}`} ref={boxRef} aria-busy={gate.waiting}>
      {hero}
      {item.workoutExecutionId === null ? null : (
        <div className={styles.stack}>
          <div className={own.detailHead}>
            <h4 className={styles.sectionTitle}>{t('web:tasks.blocks.record.inSession')}</h4>
            {view === null ? null : (
              <p className={own.legend}>
                {view.marks.has('record') ? (
                  <span className={own.legendItem}>
                    <i className={`${String(styles.set)} ${String(styles.setRecord)}`} aria-hidden="true" />
                    {t('web:tasks.blocks.record.legend.record')}
                  </span>
                ) : null}
                {view.marks.has('up') ? (
                  <span className={own.legendItem}>
                    <i className={`${String(styles.set)} ${String(styles.setUp)}`} aria-hidden="true" />
                    {t('web:tasks.blocks.record.legend.heavier')}
                  </span>
                ) : null}
              </p>
            )}
          </div>
          <div className={`${String(own.detailBox)} ${String(reservedLines)}`}>
            <BlockRead gate={gate} reserve={own.reserveDetail} failed={detailFailed} className={own.detail}>
              {view === null ? null : (
                <>
                  <ExerciseLines lines={view.lines} unchanged={view.unchanged} limit={LINE_LIMIT} markRecords />
                  <div className={`${String(styles.tiles)} ${String(own.tiles)}`}>
                    {tallies === null ? null : (
                      <>
                        <Tile label={t('web:tasks.blocks.record.tile.sets')}>
                          {t('web:tasks.blocks.record.setsOf', { done: tallies.setsDone, total: tallies.setsTotal })}
                        </Tile>
                        <Tile label={t('web:tasks.blocks.record.tile.heavier')}>{tallies.heavier}</Tile>
                        <Tile label={t('web:tasks.blocks.record.tile.skipped')}>{tallies.skipped}</Tile>
                      </>
                    )}
                    {item.signalAtUtc === null ? null : (
                      <Tile label={t('web:tasks.blocks.record.tile.ended')}>
                        {dateTimeFormat(i18n.language, { weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(
                          new Date(item.signalAtUtc),
                        )}
                      </Tile>
                    )}
                  </div>
                </>
              )}
            </BlockRead>
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <div className={styles.tile}>
      <p className={styles.tileLabel}>{label}</p>
      <p className={styles.tileValue}>{children}</p>
    </div>
  );
}
